import { Link } from "react-router-dom";
import { Heart, Star, Box, Plus } from "lucide-react";
import { useShop } from "../context/ShopContext";
import { useCompare, compareGroup } from "../context/CompareContext";
import { img, inr, off } from "../lib/api";
import { productPath } from "../lib/seo";
import MiniBox3D, { hasFullViews } from "./MiniBox3D";

const FIT = { fits: ["Fits", "bg-emerald-500 text-black"], tight: ["Tight fit", "bg-amber-400 text-black"], unknown: ["Size unknown", "bg-white/80 text-black"] };
const FitTag = ({ fit, id }) => <span data-testid={`fit-tag-${id}`} className={`absolute top-12 left-3 text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${FIT[fit][1]}`}>{FIT[fit][0]}</span>;

export default function ProductCard({ p, i = 0 }) {
  const { wish, toggleWish, addToCart } = useShop();
  const liked = wish.includes(p.id);
  const cmp = useCompare();
  return (
    <div data-testid={`product-card-${p.id}`} style={{ animationDelay: `${i * 50}ms` }}
      className="rise group relative rounded-2xl bg-[#12141c] border border-white/[0.08] hover:border-[#ff5500]/40 overflow-hidden transition-[border-color,transform] duration-300 hover:-translate-y-1">
      <Link to={productPath(p)} className="block relative aspect-[4/5] overflow-hidden bg-[#1a1d28]">
        <img src={img(p.images?.[0], 480)} alt={p.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
        {hasFullViews(p) ? (
          <span data-testid={`true-3d-badge-${p.id}`} className="absolute top-3 left-3 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-[#00f0ff] text-black px-2 py-1 rounded-full"><Box size={11} />True 3D</span>
        ) : (
          <span className="absolute top-3 left-3 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider bg-black/60 backdrop-blur px-2 py-1 rounded-full text-[#00f0ff]"><Box size={11} />3D View</span>
        )}
        {hasFullViews(p) && <MiniBox3D p={p} />}
        {p.fit && <FitTag fit={p.fit} id={p.id} />}
        {off(p) > 0 && <span className="absolute bottom-3 left-3 text-xs font-bold bg-[#ff5500] px-2 py-1 rounded-md">{off(p)}% OFF</span>}
      </Link>
      <button data-testid={`wishlist-toggle-${p.id}`} onClick={() => toggleWish(p.id)} aria-label="wishlist"
        className="absolute top-3 right-3 p-2 rounded-full bg-black/50 backdrop-blur hover:bg-black/80 transition-colors">
        <Heart size={16} className={liked ? "fill-[#ff5500] text-[#ff5500]" : "text-white"} />
      </button>
      <div className="p-4 space-y-2">
        {compareGroup(p) && (
          <label data-testid={`compare-toggle-${p.id}`} className="flex items-center gap-1.5 text-[11px] text-slate-400 cursor-pointer w-fit hover:text-white">
            <input type="checkbox" checked={cmp.has(p.id)} onChange={() => cmp.toggle(p)} className="accent-[#ff5500] w-3.5 h-3.5" />Compare
          </label>
        )}
        <p className="text-[11px] uppercase tracking-wider text-slate-500">{p.brand}</p>
        <Link to={productPath(p)} className="block font-display font-semibold leading-snug line-clamp-2 min-h-[2.6em] hover:text-[#ff5500] transition-colors">{p.title}</Link>
        <div className="flex items-center gap-1 text-xs">
          <span className="flex items-center gap-1 bg-emerald-600/90 px-1.5 py-0.5 rounded font-semibold">{p.rating || "New"}<Star size={10} className="fill-white" /></span>
          <span className="text-slate-500">({p.reviews_count})</span>
        </div>
        <div className="flex items-end justify-between gap-2">
          <div>
            <span className="font-display text-lg font-bold" data-testid={`product-price-${p.id}`}>{p.price_per_kg || p.price_per_piece ? <span className="text-xs font-normal text-slate-400 mr-1">from</span> : null}{inr(p.price)}</span>
            {p.price_per_kg > 0 && <span className="text-[11px] text-[#00f0ff]" data-testid={`product-ppk-${p.id}`}>{inr(p.price_per_kg)}/{p.weight_unit === "l" ? "L" : "kg"}</span>}
            {p.price_per_piece > 0 && <span className="text-[11px] text-[#ffb703]" data-testid={`product-ppp-${p.id}`}>{inr(p.price_per_piece)} each</span>}
            {p.mrp > p.price && <span className="ml-2 text-xs text-slate-500 line-through">{inr(p.mrp)}</span>}
          </div>
          <button data-testid={`add-to-cart-${p.id}`} onClick={() => addToCart(p)} aria-label="add to cart"
            className="p-2 rounded-full bg-white text-black hover:bg-[#ff5500] hover:text-white transition-colors"><Plus size={16} /></button>
        </div>
      </div>
    </div>
  );
}
